import { useState } from 'react'
import { clsx } from 'clsx'
import type { GwHistoryEntry, PlayerListItem } from '../types'
import styles from './PlayerStatsContent.module.css'

// ── Props ──────────────────────────────────────────────────────────────────────

type PlayerStatsContentProps = {
  player:   PlayerListItem
  isMobile: boolean
}

type StatsMode = 'main' | 'more'

// ── Helpers ────────────────────────────────────────────────────────────────────

function getInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/)
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase()
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase()
}

function fmt(n: number): string {
  return n > 0 ? String(n) : '0'
}

function csDisplay(gw: GwHistoryEntry): string {
  return gw.cleanSheet ? '✓' : '0'
}

// Compute aggregate totals across all GWs
function computeTotals(history: GwHistoryEntry[]) {
  return history.reduce(
    (acc, gw) => ({
      points:                acc.points                + gw.points,
      minutes:               acc.minutes               + gw.minutes,
      goals:                 acc.goals                 + gw.goals,
      assists:               acc.assists               + gw.assists,
      defensiveContribution: acc.defensiveContribution + gw.defensiveContribution,
      saves:                 acc.saves                 + gw.saves,
      goalsConceded:         acc.goalsConceded         + gw.goalsConceded,
      ownGoals:              acc.ownGoals              + gw.ownGoals,
      yellowCards:           acc.yellowCards           + gw.yellowCards,
      redCards:              acc.redCards              + gw.redCards,
      penaltiesSaved:        acc.penaltiesSaved        + gw.penaltiesSaved,
      successDribbles:       acc.successDribbles       + gw.successDribbles,
      penaltiesMissed:       acc.penaltiesMissed       + gw.penaltiesMissed,
      penaltiesWon:          acc.penaltiesWon          + gw.penaltiesWon,
      shotsOnTarget:         acc.shotsOnTarget         + gw.shotsOnTarget,
      cleanSheets:           acc.cleanSheets           + (gw.cleanSheet ? 1 : 0),
    }),
    {
      points: 0, minutes: 0, goals: 0, assists: 0, defensiveContribution: 0,
      saves: 0, goalsConceded: 0, ownGoals: 0, yellowCards: 0, redCards: 0,
      penaltiesSaved: 0, successDribbles: 0, penaltiesMissed: 0, penaltiesWon: 0,
      shotsOnTarget: 0, cleanSheets: 0,
    },
  )
}

// ── Component ──────────────────────────────────────────────────────────────────

export function PlayerStatsContent({ player, isMobile }: PlayerStatsContentProps) {
  const [statsMode, setStatsMode] = useState<StatsMode>('main')

  const avgDisplay = player.avgPoints != null ? player.avgPoints.toFixed(1) : '—'
  const totals     = computeTotals(player.gwHistory)
  // "Rodadas" counts only GWs the player actually played — DNP rows are history context,
  // not appearances, and must stay consistent with the backend-computed Média.
  const gwCount    = player.gwHistory.filter(gw => gw.played).length

  // ── Stats strip ──────────────────────────────────────────────────────────
  const mobileStats = [
    { label: 'Pts',   value: String(player.totalPoints),       accent: true,  gold: false },
    { label: 'Média', value: avgDisplay,                        accent: false, gold: false },
    { label: 'Gols',  value: fmt(totals.goals),                 accent: false, gold: false },
    { label: 'Ass.',  value: fmt(totals.assists),               accent: false, gold: false },
    { label: 'CS',    value: fmt(totals.cleanSheets),           accent: false, gold: false },
    { label: 'Preço', value: `$${player.price.toFixed(1)}`,     accent: false, gold: true  },
  ]

  const desktopStats = [
    { label: 'Rodadas',   value: String(gwCount),               accent: false, gold: false },
    { label: 'Pts Total', value: String(player.totalPoints),    accent: true,  gold: false },
    { label: 'Média',     value: avgDisplay,                    accent: false, gold: false },
    { label: 'Gols',      value: fmt(totals.goals),             accent: false, gold: false },
    { label: 'Assist.',   value: fmt(totals.assists),           accent: false, gold: false },
    { label: 'CS',        value: fmt(totals.cleanSheets),       accent: false, gold: false },
    { label: 'DC',        value: fmt(totals.defensiveContribution), accent: false, gold: false },
    { label: 'Preço',     value: `$${player.price.toFixed(1)}`, accent: false, gold: true  },
  ]

  const stats = isMobile ? mobileStats : desktopStats

  const stripClass    = isMobile ? styles.mStrip    : styles.aStrip
  const statClass     = isMobile ? styles.mStat     : styles.aStat
  const labelClass    = isMobile ? styles.mLabel    : styles.aLabel
  const valClass      = isMobile ? styles.mVal      : styles.aVal
  const toggleWrap    = isMobile ? styles.mToggle   : styles.toggle
  const tabClass      = isMobile ? styles.mTab      : styles.tab
  const histWrapClass = isMobile ? styles.mHistWrap : styles.histWrap
  const tableClass    = isMobile ? styles.mhTable   : styles.mhTableDesktop

  return (
    <>
      {/* Stats strip */}
      <div className={stripClass} role="list" aria-label="Estatísticas do jogador">
        {stats.map((s, i) => (
          <div key={i} className={statClass} role="listitem">
            <div className={labelClass}>{s.label}</div>
            <div className={clsx(valClass, s.accent && styles.accentVal, s.gold && styles.goldVal)}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Tab toggle */}
      <div className={toggleWrap} role="tablist">
        <button
          className={clsx(tabClass, statsMode === 'main' && styles.tabActive)}
          onClick={() => setStatsMode('main')}
          role="tab"
          aria-selected={statsMode === 'main'}
        >
          Stats Principais
        </button>
        <button
          className={clsx(tabClass, statsMode === 'more' && styles.tabActive)}
          onClick={() => setStatsMode('more')}
          role="tab"
          aria-selected={statsMode === 'more'}
        >
          Mais Stats
        </button>
      </div>

      {/* History table */}
      <div className={histWrapClass}>
        {statsMode === 'main' ? (
          <>
            <div className={isMobile ? styles.mHistTitle : styles.histTitle}>
              Histórico por Rodada
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className={tableClass} aria-label="Histórico de stats principais">
                <thead>
                  <tr>
                    <th>Rod.</th>
                    <th>Adv.</th>
                    <th>Res.</th>
                    <th>Min</th>
                    <th>Pts</th>
                    <th>Gols</th>
                    <th>Ass.</th>
                    <th>CS</th>
                    <th>DC</th>
                    <th>DG</th>
                  </tr>
                </thead>
                <tbody>
                  {player.gwHistory.map(gw => (
                    <tr key={gw.gameweekNumber}>
                      <td>{gw.gameweekNumber}</td>
                      <td>{gw.opponent ?? '—'}</td>
                      <td>{gw.result   ?? '—'}</td>
                      <td>{gw.minutes}</td>
                      <td className={styles.mhPts}>{gw.points}</td>
                      <td>{fmt(gw.goals)}</td>
                      <td>{fmt(gw.assists)}</td>
                      <td className={gw.cleanSheet ? styles.csPositive : undefined}>{csDisplay(gw)}</td>
                      <td>{fmt(gw.defensiveContribution)}</td>
                      <td>{fmt(gw.saves)}</td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  {player.gwHistory.length > 0 && (
                    <tr className={styles.totalRow}>
                      <td>Total</td>
                      <td>—</td>
                      <td>—</td>
                      <td>{totals.minutes}</td>
                      <td className={styles.mhPts}>{totals.points}</td>
                      <td>{fmt(totals.goals)}</td>
                      <td>{fmt(totals.assists)}</td>
                      <td>{fmt(totals.cleanSheets)}</td>
                      <td>{fmt(totals.defensiveContribution)}</td>
                      <td>{fmt(totals.saves)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.legend}>
              <span>Rod.=Rodada</span>
              <span>Adv.=Adversário</span>
              <span>Res.=Resultado</span>
              <span>Ass.=Assistências</span>
              <span>CS=Clean Sheet</span>
              <span>DC=Contrib. Defensiva</span>
              <span>DG=Defesas Goleiro</span>
            </div>
          </>
        ) : (
          <>
            <div className={isMobile ? styles.mHistTitle : styles.histTitle}>
              Histórico Detalhado
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className={tableClass} aria-label="Histórico de stats adicionais">
                <thead>
                  <tr>
                    <th>Rod.</th>
                    <th>Adv.</th>
                    <th>Res.</th>
                    <th>GS</th>
                    <th>GC</th>
                    <th>CA</th>
                    <th>CV</th>
                    <th>DP</th>
                    <th>DS</th>
                    <th>PP</th>
                    <th>PS</th>
                    <th>CG</th>
                  </tr>
                </thead>
                <tbody>
                  {player.gwHistory.map(gw => (
                    <tr key={gw.gameweekNumber}>
                      <td>{gw.gameweekNumber}</td>
                      <td>{gw.opponent ?? '—'}</td>
                      <td>{gw.result   ?? '—'}</td>
                      <td className={gw.goalsConceded > 0 ? styles.statNeg : undefined}>{fmt(gw.goalsConceded)}</td>
                      <td className={gw.ownGoals > 0 ? styles.statNeg : undefined}>{fmt(gw.ownGoals)}</td>
                      <td className={gw.yellowCards > 0 ? styles.statWarn : undefined}>{fmt(gw.yellowCards)}</td>
                      <td className={gw.redCards > 0 ? styles.statNeg : undefined}>{fmt(gw.redCards)}</td>
                      <td>{fmt(gw.penaltiesSaved)}</td>
                      <td>{fmt(gw.successDribbles)}</td>
                      <td className={gw.penaltiesMissed > 0 ? styles.statNeg : undefined}>{fmt(gw.penaltiesMissed)}</td>
                      <td>{fmt(gw.penaltiesWon)}</td>
                      <td>{fmt(gw.shotsOnTarget)}</td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  {player.gwHistory.length > 0 && (
                    <tr className={styles.totalRow}>
                      <td>Total</td>
                      <td>—</td>
                      <td>—</td>
                      <td>{fmt(totals.goalsConceded)}</td>
                      <td>{fmt(totals.ownGoals)}</td>
                      <td>{fmt(totals.yellowCards)}</td>
                      <td>{fmt(totals.redCards)}</td>
                      <td>{fmt(totals.penaltiesSaved)}</td>
                      <td>{fmt(totals.successDribbles)}</td>
                      <td>{fmt(totals.penaltiesMissed)}</td>
                      <td>{fmt(totals.penaltiesWon)}</td>
                      <td>{fmt(totals.shotsOnTarget)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.legend}>
              <span>GS=Gols Sofridos</span>
              <span>GC=Gol Contra</span>
              <span>CA=Cartão Amarelo</span>
              <span>CV=Cartão Vermelho</span>
              <span>DP=Defesa Penalty</span>
              <span>DS=Dribles Sucesso</span>
              <span>PP=Penalty Perdido</span>
              <span>PS=Penalty Sofrido</span>
              <span>CG=Chute a Gol</span>
            </div>
          </>
        )}
      </div>
    </>
  )
}

// Re-export helper for use by sheet
export { getInitials }
